import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";

const LandingNavbar = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const t = (obj) => obj[currentLang] || obj.fr;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className="fixed top-0 w-full z-50 backdrop-blur-md"
      style={{ backgroundColor: "rgba(1,1,2,0.85)", borderBottom: "1px solid #23252a", height: "56px" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex justify-between items-center h-full">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="/assets/brand/mawered-navbar-lockup.svg"
              alt="Mawered"
              style={{ height: "28px", width: "auto" }}
            />
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: t({ fr: "Fonctionnalités", en: "Features", ar: "الميزات" }), id: "features" },
              { label: t({ fr: "Tarifs", en: "Pricing", ar: "الأسعار" }), id: "pricing" },
              { label: "Contact", id: "contact" },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-sm font-medium transition-colors"
                style={{ color: "var(--lm-ink-2)" }}
                onMouseEnter={(e) => (e.target.style.color = "var(--lm-ink)")}
                onMouseLeave={(e) => (e.target.style.color = "var(--lm-ink-2)")}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language switcher */}
            <div className="flex items-center gap-1" style={{ borderRight: "1px solid #23252a", paddingRight: "12px", marginRight: "4px" }}>
              {["fr", "en", "ar"].map((lang) => (
                <button
                  key={lang}
                  onClick={() => i18n.changeLanguage(lang)}
                  className="px-2 py-1 rounded text-xs font-medium uppercase transition-colors"
                  style={{
                    backgroundColor: currentLang === lang ? "var(--lm-s1)" : "transparent",
                    color: currentLang === lang ? "var(--lm-ink)" : "var(--lm-ink-3)",
                    border: currentLang === lang ? "1px solid #23252a" : "1px solid transparent",
                  }}
                >
                  {lang}
                </button>
              ))}
            </div>

            <button
              onClick={() => navigate("/register")}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: "var(--lm-s1)", color: "var(--lm-ink)", border: "1px solid #23252a" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--lm-s2)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--lm-s1)")}
            >
              {t({ fr: "Essai Gratuit", en: "Free Trial", ar: "تجربة مجانية" })}
            </button>
            <button
              onClick={() => navigate("/login")}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: "#5e6ad2", color: "#ffffff" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#828fff")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
            >
              {t({ fr: "Connexion", en: "Login", ar: "تسجيل الدخول" })}
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ color: "var(--lm-ink-2)" }}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div
          className="md:hidden px-4 pb-4 pt-2 space-y-2"
          style={{ backgroundColor: "var(--lm-s1)", borderBottom: "1px solid #23252a" }}
        >
          {[
            { label: t({ fr: "Fonctionnalités", en: "Features", ar: "الميزات" }), id: "features" },
            { label: t({ fr: "Tarifs", en: "Pricing", ar: "الأسعار" }), id: "pricing" },
            { label: "Contact", id: "contact" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="block w-full text-left px-3 py-2 rounded-lg text-sm"
              style={{ color: "var(--lm-ink-2)" }}
            >
              {item.label}
            </button>
          ))}
          <div className="flex gap-2 pt-2" style={{ borderTop: "1px solid #23252a" }}>
            {["fr", "en", "ar"].map((lang) => (
              <button
                key={lang}
                onClick={() => i18n.changeLanguage(lang)}
                className="px-2 py-1 rounded text-xs font-medium uppercase"
                style={{
                  backgroundColor: currentLang === lang ? "var(--lm-s2)" : "transparent",
                  color: currentLang === lang ? "var(--lm-ink)" : "var(--lm-ink-3)",
                  border: "1px solid #23252a",
                }}
              >
                {lang}
              </button>
            ))}
          </div>
          <div className="space-y-2 pt-1">
            <button
              onClick={() => navigate("/register")}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: "var(--lm-s1)", color: "var(--lm-ink)", border: "1px solid #23252a" }}
            >
              {t({ fr: "Essai Gratuit 14 jours", en: "14-day Free Trial", ar: "تجربة مجانية 14 يومًا" })}
            </button>
            <button
              onClick={() => navigate("/login")}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: "#5e6ad2", color: "#ffffff" }}
            >
              {t({ fr: "Connexion", en: "Login", ar: "تسجيل الدخول" })}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default LandingNavbar;
